/**
 * The currency the shop itself is running in.
 *
 * There are three places a currency can come from and they can all disagree:
 * the push a pixel plugin makes, the shop's own JavaScript config, and the
 * default the customer typed into our settings screen. The last of those is the
 * one we fall back on today, and it is the only one that is not the shop.
 *
 * Measured on 6 August 2026: a shop switched to USD kept reporting EUR, because
 * Pixel Manager's add_to_cart push carries no currency at all and the fallback
 * chain ended at a settings field nobody had changed. The amount was right and
 * the unit was wrong, so an ad platform bid on a 19 dollar sale as if it were 19
 * euros, and nothing anywhere reported a problem.
 *
 * Reading it from the page is not a guess: both platforms publish it. This
 * returns undefined rather than a default, so the caller decides whether to
 * fall back, and no wrong currency is ever invented here.
 */
export function shopCurrency(): string | undefined {
  const w = window as any;

  // WooCommerce. `woocommerce_params.currency` is often assumed and does not
  // exist: that object holds four keys, none of them a currency.
  const wooSettings = w.wcSettings?.currency?.code;
  if (isCurrencyCode(wooSettings)) return wooSettings;

  // A shop running Pixel Manager states it too.
  const pixelManager = w.pmwDataLayer?.shop?.currency;
  if (isCurrencyCode(pixelManager)) return pixelManager;

  // Shopify.
  const shopify = w.Shopify?.currency?.active;
  if (isCurrencyCode(shopify)) return shopify;

  return undefined;
}

function isCurrencyCode(value: unknown): value is string {
  return typeof value === "string" && /^[A-Za-z]{3}$/.test(value);
}
